import React, { createContext, FC, ReactNode, useContext, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

interface PendingUpload {
  uploadId: string;
  file: File;
  targetFolderId: number;
}

interface PendingVersionUpload {
  uploadId: string;
  file: File;
  targetAssetId: number;
  targetAssetName: string;
  targetFolderId: number;
  targetAssetSlides: FolderManagerItem[];
}

interface UploadNewAssetContextValue {
  pendingUpload: PendingUpload | null;
  pendingVersionUpload: PendingVersionUpload | null;
  rootFolderId: number | null;
  viewStateKey?: string;
  openUploadNewAsset: (file: File, targetFolderId: number) => void;
  closeUploadNewAsset: () => void;
  openUploadNewAssetVersion: (
    file: File,
    targetAssetId: number,
    targetAssetName: string,
    targetFolderId: number,
    targetAssetSlides: FolderManagerItem[],
  ) => void;
  closeUploadNewAssetVersion: () => void;
}

const noopContextValue: UploadNewAssetContextValue = {
  pendingUpload: null,
  pendingVersionUpload: null,
  rootFolderId: null,
  openUploadNewAsset: () => {},
  closeUploadNewAsset: () => {},
  openUploadNewAssetVersion: () => {},
  closeUploadNewAssetVersion: () => {},
};

const UploadNewAssetContext = createContext<UploadNewAssetContextValue>(noopContextValue);

export const UploadNewAssetProvider: FC<{ rootFolderId: number | null; viewStateKey?: string; children: ReactNode }> = ({
  rootFolderId,
  viewStateKey,
  children,
}) => {
  const [pendingUpload, setPendingUpload] = useState<PendingUpload | null>(null);
  const [pendingVersionUpload, setPendingVersionUpload] = useState<PendingVersionUpload | null>(null);

  const value = useMemo<UploadNewAssetContextValue>(
    () => ({
      pendingUpload,
      pendingVersionUpload,
      rootFolderId,
      viewStateKey,
      openUploadNewAsset: (file, targetFolderId) => setPendingUpload({ uploadId: uuidv4(), file, targetFolderId }),
      closeUploadNewAsset: () => setPendingUpload(null),
      openUploadNewAssetVersion: (file, targetAssetId, targetAssetName, targetFolderId, targetAssetSlides) =>
        setPendingVersionUpload({
          uploadId: uuidv4(),
          file,
          targetAssetId,
          targetAssetName,
          targetFolderId,
          targetAssetSlides,
        }),
      closeUploadNewAssetVersion: () => setPendingVersionUpload(null),
    }),
    [pendingUpload, pendingVersionUpload, rootFolderId, viewStateKey],
  );

  return <UploadNewAssetContext.Provider value={value}>{children}</UploadNewAssetContext.Provider>;
};

export const useUploadNewAsset = () => useContext(UploadNewAssetContext);
